import React from 'react';
import { murmurhash } from '../../lib/js/hash';
import { TooltipPlacement } from '../../components/atoms/Tooltip';
import { BlockType, ElementType } from '../../types/api/ElementType';
import { AttachedFAQFilesBlock } from './FaqAttachedFiles/FaqAttachedFiles';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';

import {
  Accordion,
  AccordionElement,
  AccordionHeader,
  AccordionHeading,
  AccordionIcon,
  AccordionItemExtraButton,
  AccordionPanel,
  AccordionTitle,
  AccordionToggleButtonWithTooltip,
} from '../../_lib/components/Accordion';

import { StateIndicator } from '../../_lib/components/StateIndicator';
import { PagePackState, PagePackStates } from '../../types/api/PagePackState';
import { AllowedFAQBlocks, FetchedFAQBlocks, isNotFetched, useCacheEntries } from './useLazyBlockInstantMapper';
import { LoadingSpinner } from '../../components/atoms/LoadingSpinner';
import { On2ContentBlock } from '../On2Content/On2ContentBlock';
import { useFaqAccordion } from './useFaqAccordion';
import { CollapsibleExpanderButtonBar } from '../../_lib/components/CollapsibleExpander';
import { FaqContentWrapperBlock } from './FaqContentWrapper/FaqContentWrapperBlock';
import { isEmptyBlock, isFetchError } from '../../types/api/Block';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { asBlock } from '../../_lib/BlockFetcher';
import { ChildPageMetaInfoBlock } from './ChildPageMetaInfoBlock/ChildPageMetaInfoBlock';

type FaqEntryProps = {
  title: string;
  edit?: { url: string };
  packState?: PagePackStates;
  content: AllowedFAQBlocks[];
};

export type FaqListProps = {
  faqEntries: FaqEntryProps[];
};

function FaqItemChild(props: FetchedFAQBlocks) {
  if (isFetchError(props) || isEmptyBlock(props)) {
    return null;
  }
  switch (props.elementType) {
    case BlockType.childPageMetaInfo:
      return <ChildPageMetaInfoBlock {...props} />;
    case ElementType.contentWrapper:
      return <FaqContentWrapperBlock {...props} />;
    case BlockType.attachedfiles:
      return <AttachedFAQFilesBlock {...props} />;
    case BlockType.on2content:
      return <On2ContentBlock {...props} />;
    default:
      return null;
  }
}
export function FaqList({ faqEntries }: FaqListProps) {
  const { t } = useSonTranslation(Namespace.FaqList);
  const openTranslation = (title: string) => t(`${Namespace.FaqList}:openFaqListPost`, { title });
  const data = faqEntries.map((faqEntry, index) => ({
    id: `faq-list-element-${murmurhash(faqEntry.title + index)}`,
    category: faqEntry.title,
    content: faqEntry.content,
    edit: faqEntry.edit,
    title: faqEntry.title,
    isDraft: faqEntry.packState === PagePackState.ONLY_DRAFT_VERSION || undefined,
  }));

  const { getAccordionProps, items, activeElementIds, closeAll, openAll } = useFaqAccordion(data);
  const fetchedBlocks = useCacheEntries(data, activeElementIds);

  return (
    <>
      {items.length > 1 && (
        <CollapsibleExpanderButtonBar
          collapseAllOnClick={closeAll}
          collapseAllButtonDisabled={activeElementIds.length === 0}
          expandAllOnClick={openAll}
          expandAllButtonDisabled={activeElementIds.length === items.length}
          ariaControls={items.map(el => el.panelId).join(' ')}
        />
      )}
      <Accordion {...getAccordionProps()}>
        {items.map(item => (
          <AccordionElement {...item.getElementProps()} key={`AccordionElement-${item.id}`}>
            <AccordionHeader>
              <AccordionHeading>
                <AccordionToggleButtonWithTooltip
                  tooltipPlacement={TooltipPlacement.TOP_START}
                  title={item.isDraft && t(`${Namespace.glossary}:draft`)}
                  {...item.getToggleButtonProps()}
                >
                  {item.isDraft && <StateIndicator status="red" />}
                  <AccordionTitle category={item.category} modifier={item.edit && 'with-button'} />
                  <AccordionIcon />
                </AccordionToggleButtonWithTooltip>
              </AccordionHeading>
              {item.edit && (
                <AccordionItemExtraButton
                  text={openTranslation(item.title)}
                  href={item.edit.url}
                  svgSpriteId={Icons.EDIT_PENCIL}
                />
              )}
            </AccordionHeader>
            <AccordionPanel {...item.getPanelProps()}>
              <div className="faq-list__white-panel-background">
                {activeElementIds.includes(item.id) &&
                  fetchedBlocks
                    .filter(entry => entry.id === item.id)
                    .flatMap(entry =>
                      isNotFetched(entry)
                        ? (
                            <div className="faq-list__spinner" key={entry.id}>
                              <LoadingSpinner />
                            </div>
                          )
                        : (
                            entry.blocks.map(block => <FaqItemChild {...block} key={entry.id + block.elementType} />)
                          ),
                    )}
              </div>
            </AccordionPanel>
          </AccordionElement>
        ))}
      </Accordion>
    </>
  );
}

export const FaqListBlock = asBlock(FaqList, BlockType.faqlist);
